import { sanitizeNumber } from '@shared/utils/number';
import type { CargoPortWithTurnDays } from '@api/features/laytime/getCargosAndLaytimes';
import { CargoProps } from '@api/models/cargo';
import { splitActionKey } from './splitActionKey';
import { calculateTotal, ensureNegative } from './math';
import { LaytimeIntervals, LaytimeSettlementTypes } from './constants';
import normalizeNumber from './normalizeNumber';
import { sortTimesheet } from './laytimeStats/helpers';

export const getCargoPortLaytimeStats = (
	cargoPort: CargoPortWithTurnDays,
	cargo: CargoProps,
	includeSettlement = true,
) => {
	const { action } = splitActionKey(cargoPort.portAndActionKey);
	let demurrage = 0;
	let calculatedDemurrage = 0;
	let despatch = 0;
	let calculatedDespatch = 0;
	let timeInDays = 0;

	if (
		(!cargoPort?.Timesheet?.TimesheetRows ||
		!cargoPort.laytimeFrom ||
		!cargoPort.laytimeTo) &&
		cargoPort.settlementAmount == null
	) {
		return {
			action,
			timeAllowedInMinutes: 0,
			timeInDays: 0,
			timeGainedOrLost: 0,
			isDem: true,
			totalDemurrage: demurrage,
			totalDespatch: despatch,
			isSettlement: false,
			calculatedDemurrage,
			calculatedDespatch,
			calculatedDemurrageLoading: 0,
			calculatedDemurrageDischarging: 0,
			calculatedDespatchLoading: 0,
			calculatedDespatchDischarging: 0,
		};
	}

	const timesheetRows = sortTimesheet(cargoPort?.Timesheet?.TimesheetRows ?? []);

	const totalTime = calculateTotal(timesheetRows, (c) => c.totalCountingMinutes);
	const timeAllowed = cargoPort.estimatedTimeAllowed;
	let timeAllowedInMinutes = normalizeNumber(timeAllowed) * 60;

	if (cargoPort.laytimeInterval === LaytimeIntervals.DAY) {
		timeAllowedInMinutes *= 24;
	}

	const demurrageRow = timesheetRows?.find((tr) => tr.demurrageStarts);

	if (demurrageRow && cargoPort.laytimeTo) {
		const demurrageRowIndex = timesheetRows.findIndex((tr) => tr.id === demurrageRow.id);
		const demurrageSlice = timesheetRows.slice(demurrageRowIndex);
		const minutesUsed = calculateTotal(demurrageSlice, (c) => c.totalCountingMinutes);

		const timeConversion = cargo.demurrageDespatchInterval === LaytimeIntervals.DAY ? 60 * 24 : 60;
		timeInDays = minutesUsed / timeConversion;

		const dem = timeInDays * sanitizeNumber(cargoPort.demurrage);

		demurrage = dem;
	} else {
		const timeConversion = cargo.demurrageDespatchInterval === LaytimeIntervals.DAY ? 60 * 24 : 60;
		timeInDays = totalTime / timeConversion;

		const des = (sanitizeNumber(cargoPort.estimatedTimeAllowed) - timeInDays) * sanitizeNumber(cargoPort.despatch);
		despatch = des;
	}

	calculatedDemurrage = demurrage;
	calculatedDespatch = despatch;

	if (cargoPort.settlementAmount != null && includeSettlement) {
		if (cargoPort.settlementType === LaytimeSettlementTypes.DEMURRAGE) {
			demurrage = cargoPort.settlementAmount;
		} else {
			despatch = ensureNegative(cargoPort.settlementAmount);
		}
	}

	let isDem = demurrage > 0;

	if (cargoPort.settlementAmount != null) {
		isDem = cargoPort.settlementType === LaytimeSettlementTypes.DEMURRAGE;
	}

	return {
		action,
		timeAllowedInMinutes,
		timeInDays,
		timeGainedOrLost: timeAllowedInMinutes - totalTime,
		isDem, 
		totalDemurrage: demurrage,
		totalDespatch: despatch,
		isSettlement: cargoPort.settlementAmount != null,
		calculatedDemurrage,
		calculatedDespatch,
		calculatedDemurrageLoading: 0,
		calculatedDemurrageDischarging: 0,
		calculatedDespatchLoading: 0,
		calculatedDespatchDischarging: 0,
	};
};
